import React, { useState, useRef, useEffect } from 'react';
import { doc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { getEmbedding, calculateCosineSimilarity } from '../utils/embeddings';
import SimilarNotes from './SimilarNotes';

function EssayEditor({ essay }) {
  const [title, setTitle] = useState(essay.title);
  const [body, setBody] = useState(essay.body);
  const [status, setStatus] = useState(essay.status);
  const [isSaving, setIsSaving] = useState(false);
  const [similarNotes, setSimilarNotes] = useState([]);
  const [similarityThreshold, setSimilarityThreshold] = useState(0.2);
  const [isMatrixLoading, setIsMatrixLoading] = useState(false);
  const [similarityMatrix, setSimilarityMatrix] = useState([]);
  
  const debouncedUpdate = useRef(null);

  // Add effect to update local state when essay prop changes
  useEffect(() => {
    setTitle(essay.title);
    setBody(essay.body);
    setStatus(essay.status || 'draft');
  }, [essay]); // Re-run when essay changes

  // Add effect to find similar notes when essay changes or when first loaded
  useEffect(() => {
    if (essay && essay.embedding) {
      handleFindSimilar();
    }
  }, [essay.id, essay.embedding]); // Run when essay or its embedding changes

  const handleSave = async (newTitle = title, newBody = body, newStatus = status) => {
    if (debouncedUpdate.current) {
      clearTimeout(debouncedUpdate.current);
    }

    debouncedUpdate.current = setTimeout(async () => {
      try {
        setIsSaving(true);
        // Generate embedding for the combined title and body using the new values
        const embedding = await getEmbedding(`${newTitle}\n\n${newBody}`);
        
        const updatedData = {
          title: newTitle,
          body: newBody,
          status: newStatus,
          embedding,
          updatedAt: new Date().toISOString(),
          uid: auth.currentUser.uid
        };

        // Update Firestore
        const docRef = doc(db, "essays", essay.id);
        await updateDoc(docRef, updatedData);

        // Update local essay object
        Object.assign(essay, updatedData);
        
        // Recalculate similarities after saving
        await handleFindSimilar();
      } catch (error) {
        console.error("Error updating essay:", error);
      } finally {
        setIsSaving(false);
      }
    }, 1000);
  };

  // Update handlers to pass new values directly to handleSave
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    handleSave(newTitle, body, status);
  };

  const handleBodyChange = (e) => {
    const newBody = e.target.value;
    setBody(newBody);
    handleSave(title, newBody, status);
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    if (value === 'delete') {
      if (window.confirm('Are you sure you want to delete this essay? This action cannot be undone.')) {
        handleDelete();
      }
    } else {
      setStatus(value);
      handleSave(title, body, value);
    }
  };

  // Add delete handler
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this essay? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, "essays", essay.id));
        // Redirect to home or essays list
        window.location.href = '/essays';
      } catch (error) {
        console.error("Error deleting essay:", error);
        alert('Failed to delete essay');
      }
    }
  };

  // Update handleFindSimilar to handle edge cases better
  const handleFindSimilar = async () => {
    try {
      setIsMatrixLoading(true);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.warn('No user logged in');
        setSimilarNotes([]);
        return;
      }
      
      if (!essay || !essay.embedding) {
        console.warn('No embedding found for current essay');
        setSimilarNotes([]); // Clear similar notes if no embedding
        return;
      }

      // Fetch notes for current user
      const notesQuery = query(
        collection(db, "notes"),
        where("uid", "==", currentUser.uid)
      );
      
      const notesSnapshot = await getDocs(notesQuery);
      const userNotes = notesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'note'
      })).filter(note => note.embedding); // Only include notes with embeddings

      // Fetch essays for current user
      const essaysQuery = query(
        collection(db, "essays"),
        where("uid", "==", currentUser.uid)
      );
      
      const essaysSnapshot = await getDocs(essaysQuery);
      const userEssays = essaysSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'essay'
      })).filter(essay => essay.embedding); // Only include essays with embeddings

      // Calculate similarities with other essays and notes
      const similarItems = [...userEssays, ...userNotes]
        .filter(item => item.id !== essay.id) // Remove current essay
        .map(item => {
          try {
            return {
              ...item,
              similarity: calculateCosineSimilarity(essay.embedding, item.embedding)
            };
          } catch (error) {
            console.warn(`Error calculating similarity for item ${item.id}:`, error);
            return null;
          }
        })
        .filter(item => item && item.similarity && item.similarity >= similarityThreshold)
        .sort((a, b) => b.similarity - a.similarity);

      setSimilarNotes(similarItems);
    } catch (error) {
      console.error('Error finding similar items:', error);
      setSimilarNotes([]); // Clear similar notes on error
    } finally {
      setIsMatrixLoading(false);
    }
  };

  return (
    <div className="flex-1 bg-white flex flex-row">
      <div className="flex-1 min-w-0 flex flex-col max-h-screen">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <select
              value={status}
              onChange={handleStatusChange}
              className="border rounded-lg px-2 py-1 text-gray-700"
            >
              <option value="draft">Draft</option>
              <option value="live">Live</option>
              <option value="delete" className="text-red-600">Delete Essay</option>
            </select>
            {isSaving && <span className="text-sm text-gray-500">Saving...</span>}
          </div>
        </div>
        
        {/* Main editor area - add paddingBottom for app bar */}
        <div 
          className="flex-1 overflow-y-auto p-4"
          style={{ paddingBottom: 'var(--app-bar-height, 120px)' }}
        >
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            className="w-full text-2xl font-bold mb-4 border-none focus:outline-none"
            placeholder="Essay Title"
          />
          <textarea
            value={body}
            onChange={handleBodyChange}
            className="w-full h-full resize-none border-none focus:outline-none"
            placeholder="Start writing your essay..."
          />
        </div>
      </div>

      {/* Similar Notes Panel - add max height and paddingBottom for app bar */}
      <div 
        className="w-[440px] border-l border-gray-200 overflow-y-auto max-h-screen"
        style={{ paddingBottom: 'var(--app-bar-height, 120px)' }}
      >
        <SimilarNotes
          activeNoteId={essay.id}
          similarityMatrix={similarityMatrix}
          notes={[]}
          isMatrixLoading={isMatrixLoading}
          similarityThreshold={similarityThreshold}
          setSimilarityThreshold={setSimilarityThreshold}
          similarNotes={similarNotes}
          setSimilarNotes={setSimilarNotes}
          onFindSimilar={handleFindSimilar}
          onClose={() => {}}
        />
      </div>
    </div>
  );
}

export default EssayEditor; 